/**
 * Native alarm bridge seam for the Android shell.
 *
 * The Android WebView is expected to inject `window.LuminisNativeAlarms` before
 * the PWA bundle runs (see the typed interface below). When present, alarms are
 * scheduled/cancelled through the shell's AlarmManager implementation. When it
 * is absent (normal browser/PWA), the helper falls back to an in-page
 * `setTimeout` + `Notification` reminder. That fallback is explicitly
 * unreliable: browsers suspend timers in background tabs and on a locked phone,
 * so it is a placeholder until the shell takes over.
 *
 * No Capacitor/plugin import is used here; the shell owns the bridge shape.
 */
import { effectiveAlarmNextAt, formatAlarmTime, nextAlarmAt } from './alarm';
import type { Alarm } from './types';
import { isRecord } from './validation';

/** Event dispatched in the page when a fallback timer fires (UI banner). */
export const LUMINIS_ALARM_FIRED_EVENT = 'luminis:alarm-fired';

/** Copy shown in the alarm UI while the fallback scheduler is in use. */
export const FALLBACK_ALARM_NOTICE =
  'PWA 页面提醒不可靠：锁屏、切后台或系统省电时 setTimeout/通知可能被暂停。需要可靠闹钟请使用 Android 壳（会注入原生闹钟桥）。';

/** Large delays are chained; also keeps the value inside the 32-bit timer limit. */
export const MAX_FALLBACK_TIMEOUT_MS = 2_147_000_000;

/** Plain JSON payload handed to the native shell (epoch seconds). */
export interface LuminisNativeAlarmPayload {
  id: string;
  label: string;
  hour: number;
  minute: number;
  repeat_days: number[];
  /** Next trigger, epoch seconds (daemon value when fresh, else local preview). */
  next_at: number;
  sound: string;
}

/**
 * Bridge contract injected by the Android shell. Methods are synchronous
 * primitives so `addJavascriptInterface` can expose them directly:
 * `schedule`/`cancel` should return `false` when they reject the request.
 * `setOnFired` lets the shell notify the PWA after an alarm actually fires so
 * the daemon can advance `next_at`.
 */
export interface LuminisNativeAlarms {
  readonly version?: number;
  readonly platform?: string;
  schedule(alarm: LuminisNativeAlarmPayload): boolean;
  cancel(alarmId: string): boolean;
  cancelAll?(): boolean;
  requestNotificationPermission?(): boolean;
  setOnFired?(listener: ((alarmId: string) => void) | null): void;
}

declare global {
  interface Window {
    /** Injected by the Android shell; absent in a normal browser. */
    LuminisNativeAlarms?: LuminisNativeAlarms;
    /**
     * Fire callback the shell may invoke from native code, e.g.
     * `webView.evaluateJavascript("window.LuminisNativeAlarmFired('id')")`.
     * Installed by `subscribeNativeAlarmFired`; a JS wrapper bridge may instead
     * use `LuminisNativeAlarms.setOnFired`.
     */
    LuminisNativeAlarmFired?: (alarmId: string) => void;
  }
}

export interface AlarmFiredEventDetail {
  id: string;
  label: string;
  at: number;
}

export function isNativeAlarmBridge(value: unknown): value is LuminisNativeAlarms {
  if (!isRecord(value)) return false;
  return typeof value.schedule === 'function' && typeof value.cancel === 'function';
}

/** Read the injected bridge, or null when the PWA runs without the shell. */
export function getNativeAlarmBridge(): LuminisNativeAlarms | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as { LuminisNativeAlarms?: unknown }).LuminisNativeAlarms;
  return isNativeAlarmBridge(candidate) ? candidate : null;
}

export type AlarmDeliveryChannel = 'native' | 'pwa-fallback';

export function alarmDeliveryChannel(
  bridge: LuminisNativeAlarms | null = getNativeAlarmBridge(),
): AlarmDeliveryChannel {
  return bridge === null ? 'pwa-fallback' : 'native';
}

export type AlarmNotificationPermission = NotificationPermission | 'unsupported';

/** Notification permission for the fallback channel (UI copy only). */
export function fallbackNotificationPermission(): AlarmNotificationPermission {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return Notification.permission;
  } catch {
    return 'unsupported';
  }
}

export interface AlarmTimerApi {
  setTimeout(handler: () => void, delayMs: number): unknown;
  clearTimeout(handle: unknown): void;
}

const defaultTimerApi: AlarmTimerApi = {
  setTimeout: (handler, delayMs) => setTimeout(handler, delayMs),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface AlarmSyncOptions {
  /** `null` forces the fallback; omitted auto-detects the injected bridge. */
  bridge?: LuminisNativeAlarms | null;
  /** Called after a reminder fires (used to ack `/alarms/:id/fired`). */
  onFire?(alarmId: string): void;
  /** Epoch milliseconds (tests inject a fixed clock). */
  now?(): number;
  timers?: AlarmTimerApi;
  /** Override how the fallback notifies (tests spy on this). */
  notify?(alarm: Alarm): void;
  /** Override the in-page event dispatch (tests spy on this). */
  dispatch?(alarm: Alarm): void;
}

export interface AlarmSyncResult {
  mode: AlarmDeliveryChannel;
  scheduled: number;
  /** Alarm ids the channel rejected (native `false` / unusable time). */
  failed: string[];
}

let fallbackTimers = new Map<string, unknown>();
let fallbackTimersApi: AlarmTimerApi = defaultTimerApi;
let nativeScheduledIds = new Set<string>();

function clearFallbackTimers(api: AlarmTimerApi = fallbackTimersApi): void {
  for (const handle of fallbackTimers.values()) {
    try {
      api.clearTimeout(handle);
    } catch {
      /* timer already gone */
    }
  }
  fallbackTimers = new Map();
}

/** Cancel only the in-page reminders (native alarms keep running in the shell). */
export function cancelFallbackReminders(api: AlarmTimerApi = fallbackTimersApi): void {
  clearFallbackTimers(api);
}

/** Test hook: forget every timer/tracked id without touching a real bridge. */
export function resetAlarmSchedulerForTests(): void {
  clearFallbackTimers();
  fallbackTimersApi = defaultTimerApi;
  nativeScheduledIds = new Set();
}

function toNativePayload(alarm: Alarm, nextAt: number): LuminisNativeAlarmPayload {
  return {
    id: alarm.id,
    label: alarm.label,
    hour: alarm.hour,
    minute: alarm.minute,
    repeat_days: [...alarm.repeat_days],
    next_at: nextAt,
    sound: alarm.sound,
  };
}

function dispatchFallbackFired(alarm: Alarm, options: AlarmSyncOptions): void {
  if (options.dispatch !== undefined) {
    options.dispatch(alarm);
    return;
  }
  if (typeof window === 'undefined') return;
  try {
    const detail: AlarmFiredEventDetail = {
      id: alarm.id,
      label: alarm.label,
      at: Math.floor(Date.now() / 1000),
    };
    window.dispatchEvent(
      new CustomEvent<AlarmFiredEventDetail>(LUMINIS_ALARM_FIRED_EVENT, { detail }),
    );
  } catch {
    /* CustomEvent unavailable (very old WebView) */
  }
}

function notifyFallback(alarm: Alarm, options: AlarmSyncOptions): void {
  if (options.notify !== undefined) {
    options.notify(alarm);
  } else if (typeof Notification !== 'undefined') {
    try {
      if (Notification.permission === 'granted') {
        new Notification(alarm.label || 'Luminis 闹钟', {
          body: `${formatAlarmTime(alarm.hour, alarm.minute)} · Luminis`,
          tag: `luminis-alarm-${alarm.id}`,
        });
      }
    } catch {
      /* Notification constructor may be blocked in a WebView */
    }
  }
  dispatchFallbackFired(alarm, options);
}

function armFallbackTimer(alarm: Alarm, nextAt: number, options: AlarmSyncOptions): void {
  const timers = options.timers ?? defaultTimerApi;
  const now = options.now ?? Date.now;
  const delay = Math.max(0, nextAt * 1000 - now());
  const schedule = (delayMs: number): void => {
    const handle = timers.setTimeout(() => {
      fallbackTimers.delete(alarm.id);
      notifyFallback(alarm, options);
      try {
        options.onFire?.(alarm.id);
      } catch {
        /* never let a listener break scheduling */
      }
      if (alarm.repeat_days.length > 0) {
        const again = nextAlarmAt(alarm, Math.floor(now() / 1000) + 1);
        if (again !== null) armFallbackTimer(alarm, again, options);
      }
    }, delayMs);
    fallbackTimers.set(alarm.id, handle);
  };
  if (delay > MAX_FALLBACK_TIMEOUT_MS) {
    // Re-arm in chunks so a long weekly alarm does not overflow setTimeout.
    schedule(MAX_FALLBACK_TIMEOUT_MS);
    return;
  }
  schedule(delay);
}

function scheduleFallback(alarms: readonly Alarm[], options: AlarmSyncOptions): AlarmSyncResult {
  const timers = options.timers ?? defaultTimerApi;
  fallbackTimersApi = timers;
  clearFallbackTimers(timers);
  const now = options.now ?? Date.now;
  const nowSeconds = Math.floor(now() / 1000);
  let scheduled = 0;
  const failed: string[] = [];
  for (const alarm of alarms) {
    if (!alarm.enabled) continue;
    const nextAt = effectiveAlarmNextAt(alarm, nowSeconds);
    if (nextAt === null) {
      failed.push(alarm.id);
      continue;
    }
    armFallbackTimer(alarm, nextAt, options);
    scheduled += 1;
  }
  return { mode: 'pwa-fallback', scheduled, failed };
}

function scheduleNative(
  alarms: readonly Alarm[],
  bridge: LuminisNativeAlarms,
  options: AlarmSyncOptions,
): AlarmSyncResult {
  clearFallbackTimers();
  const now = options.now ?? Date.now;
  const nowSeconds = Math.floor(now() / 1000);
  const scheduledIds = new Set<string>();
  const failed: string[] = [];
  for (const alarm of alarms) {
    if (!alarm.enabled) continue;
    const nextAt = effectiveAlarmNextAt(alarm, nowSeconds);
    if (nextAt === null) {
      failed.push(alarm.id);
      continue;
    }
    try {
      const accepted = bridge.schedule(toNativePayload(alarm, nextAt));
      if (accepted === false) {
        failed.push(alarm.id);
        continue;
      }
      scheduledIds.add(alarm.id);
    } catch {
      failed.push(alarm.id);
    }
  }
  // Cancel alarms that were removed/disabled since the previous sync.
  for (const id of nativeScheduledIds) {
    if (scheduledIds.has(id)) continue;
    try {
      bridge.cancel(id);
    } catch {
      /* shell may already have dropped it */
    }
  }
  nativeScheduledIds = scheduledIds;
  return { mode: 'native', scheduled: scheduledIds.size, failed };
}

/**
 * Reconcile the device's schedules with the current alarm list. Safe to call on
 * every alarm state change: fallback timers are replaced, native alarms are
 * upserted by id and removed alarms are cancelled.
 */
export function syncAlarmSchedules(
  alarms: readonly Alarm[],
  options: AlarmSyncOptions = {},
): AlarmSyncResult {
  const bridge = options.bridge === undefined ? getNativeAlarmBridge() : options.bridge;
  if (bridge !== null) {
    if (options.timers !== undefined) fallbackTimersApi = options.timers;
    return scheduleNative(alarms, bridge, options);
  }
  return scheduleFallback(alarms, options);
}

/**
 * Cancel everything this device scheduled. Used on unpair/reset so a removed
 * alarm never rings after its data was cleared.
 */
export function cancelAllAlarmSchedules(
  options: Pick<AlarmSyncOptions, 'bridge' | 'timers'> = {},
): void {
  clearFallbackTimers(options.timers ?? fallbackTimersApi);
  const bridge = options.bridge === undefined ? getNativeAlarmBridge() : options.bridge;
  if (bridge !== null) {
    try {
      if (bridge.cancelAll !== undefined) bridge.cancelAll();
      else for (const id of nativeScheduledIds) bridge.cancel(id);
    } catch {
      /* best effort during teardown */
    }
  }
  nativeScheduledIds = new Set();
}

/**
 * Subscribe to native fire callbacks. Supports both shell styles:
 * - a JS wrapper bridge exposing `setOnFired(listener)`, and
 * - a plain `addJavascriptInterface` bridge where native code calls
 *   `window.LuminisNativeAlarmFired(id)` (installed here).
 * Returns a cleanup that removes both hooks.
 */
export function subscribeNativeAlarmFired(
  listener: (alarmId: string) => void,
  bridge: LuminisNativeAlarms | null = getNativeAlarmBridge(),
): () => void {
  const bridgeHook = bridge?.setOnFired !== undefined;
  if (!bridgeHook && typeof window !== 'undefined') {
    try {
      window.LuminisNativeAlarmFired = listener;
    } catch {
      /* read-only WebView global */
    }
  }
  if (bridgeHook) {
    try {
      bridge.setOnFired?.(listener);
    } catch {
      /* fall back to the global callback */
      if (typeof window !== 'undefined') {
        try {
          window.LuminisNativeAlarmFired = listener;
        } catch {
          /* ignore */
        }
      }
    }
  }
  return () => {
    if (typeof window !== 'undefined') {
      try {
        if (window.LuminisNativeAlarmFired === listener) delete window.LuminisNativeAlarmFired;
      } catch {
        /* ignore */
      }
    }
    try {
      bridge?.setOnFired?.(null);
    } catch {
      /* shell teardown */
    }
  };
}
